import { useEffect, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useProducts } from '@/features/products/hooks/use-products'

export interface SelectedProduct {
  sku: string
  name: string
  /**
   * The picked product's stock facts, as the product list carries them
   * (CHG-2026-09-stock-usage-and-price-twins): absent on a product that was not picked here.
   */
  fractionalCounting?: boolean
  stockUnit?: string
  stockTracked?: boolean
  isDish?: boolean
  variantSourceSku?: string
}

interface ProductSkuComboboxProps {
  value: SelectedProduct | null
  onChange: (product: SelectedProduct | null) => void
  onSelect?: () => void
  onAutoFocusComplete?: () => void
  placeholder?: string
  disabled?: boolean
  autoFocus?: boolean
  /**
   * Restrict the search to products assigned to this restaurant. Deliveries require it
   * (only assigned SKUs may be booked); the movement modal leaves it unset and searches
   * the whole catalog as before.
   */
  restaurantId?: number
}

export function ProductSkuCombobox({
  value,
  onChange,
  onSelect,
  onAutoFocusComplete,
  placeholder,
  disabled = false,
  autoFocus = false,
  restaurantId,
}: ProductSkuComboboxProps) {
  const { t: tProducts } = useTranslation('products')
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)

  const { data: productsData } = useProducts({
    search: searchQuery || undefined,
    pageSize: 20,
    restaurantId,
  })

  const products = productsData?.items ?? []

  useEffect(() => {
    if (autoFocus && !disabled) {
      const timer = setTimeout(() => {
        triggerRef.current?.click()
        onAutoFocusComplete?.()
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [autoFocus, disabled, onAutoFocusComplete])

  const handleSelect = (product: SelectedProduct) => {
    onChange({
      sku: product.sku,
      name: product.name,
      ...(product.fractionalCounting === undefined
        ? {}
        : {
            fractionalCounting: product.fractionalCounting,
            stockUnit: product.stockUnit,
            stockTracked: product.stockTracked,
            isDish: product.isDish,
            variantSourceSku: product.variantSourceSku,
          }),
    })
    setOpen(false)
    setSearchQuery('')
    onSelect?.()
  }

  const displayValue = value
    ? `${value.name} (${value.sku})`
    : (placeholder ?? tProducts('combobox.placeholder'))

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={value ? '' : 'text-muted-foreground'}>
            {displayValue}
          </span>
          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={tProducts('combobox.searchPlaceholder')}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>{tProducts('combobox.empty')}</CommandEmpty>
            <CommandGroup>
              {products.map((product: any) => (
                <CommandItem
                  key={product.sku}
                  value={product.sku}
                  onSelect={() => handleSelect(product)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{product.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {product.sku}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
